/* Crystal Quest water softener sizing engine (independent model, v1.0).
 *
 * Used by the water softener size calculator (section cq-softener-size, page /pages/water-softener-size-calculator).
 * Pure math, no DOM: window.CQSoftenerSizing in the browser, module.exports in Node (tools/test-softener-sizing.mjs).
 *
 * AJ 2026-10-14 18:51 ET asked for a model built from outside science and the products' own rated limits, independent
 * of Crystal Quest's other tools, with preference deciding part of the answer. Every constant below names its source.
 * The research record (fetched sources, quotes, derivations) is in the Brain:
 * 30-Targets/Projects/softener-size-calculator-2026-10/working/research/ (C1, C2, round2/R1 to R5) and the model note
 * deliverables/MODEL.md. Change a number here only together with that record.
 *
 * HOW THE ANSWER IS BUILT
 *   1. Hardness in gpg from any unit, plus what the softener must also remove (trace ferrous iron and manganese) and
 *      any hardness an acid neutralizer ahead of it adds back.
 *   2. Softened water a day from measured household use (it grows less than one-for-one with each extra person),
 *      or from a water bill.
 *   3. Peak flow from the home's fixtures with the IAPMO single-family probability model (the basis of the plumbing
 *      code's peak water demand calculator), plus continuous loads.
 *   4. Each tank's capacity at a salt setting from the resin's capacity curve, never above its printed grain rating.
 *   5. Days between regenerations with one day of reserve, salt and drain water a month, and the pressure drop at the
 *      home's peak flow, for every size. The visitor's priority picks the match; every size that works stays visible.
 */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.CQSoftenerSizing = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const VERSION = '1.1.1';

  /* ------------------------------------------------------------------------------------------ constants */
  const C = {
    // 1 grain = 64.79891 mg and 1 US gallon = 3.785412 L, so 1 gpg = 17.11806 mg/L (NIST SP 811 Appendix B.8).
    MGL_PER_GPG: 17.118,
    UNITS: {
      gpg: {factor: 1, label: 'gpg', max: 150},
      mgl: {factor: 1 / 17.118, label: 'mg/L (ppm)', max: 2568},        // as CaCO3
      dh: {factor: 17.848 / 17.118, label: 'German degrees', max: 144},   // 1 degree dH = 10 mg/L CaO = 17.848 mg/L CaCO3
      fh: {factor: 10 / 17.118, label: 'French degrees', max: 256},       // 1 degree fH = 10 mg/L CaCO3
      mmol: {factor: 100.09 / 17.118, label: 'mmol/L', max: 25.6}         // 1 mmol/L = 100.09 mg/L as CaCO3
    },
    // Hardness bands in gpg (Penn State Extension and NDSU: soft < 1, slightly 1 to 3.5, moderately 3.5 to 7,
    // hard 7 to 10.5, very hard above 10.5).
    BANDS: [
      {max: 1, key: 'soft', label: 'Soft'},
      {max: 3.5, key: 'slight', label: 'Slightly hard'},
      {max: 7, key: 'moderate', label: 'Moderately hard'},
      {max: 10.5, key: 'hard', label: 'Hard'},
      {max: Infinity, key: 'very', label: 'Very hard'}
    ],
    ASSUMED_GPG: 10,            // used only when the visitor has no number; always labelled "assumed"

    // ---- daily softened water (R1). Indoor use per HOUSEHOLD is a fixed base plus a smaller amount per person
    // (Water Research Foundation REU1999, 1,188 data-logged homes: 69.2 + 37.2 x persons, R squared 0.99), and
    // indoor use per person has fallen since (REU2016 58.6 gpcd; 138 gal a day at 2.65 people). Typical =
    // the REU1999 shape scaled to the REU2016 level, 60 + 30 x people (139.5 gal a day at 2.65 people against the
    // measured 138; about the 60th percentile of REU2016 homes, so it carries headroom). Water-saving x 0.75,
    // high use x 1.35. A winter water bill beats every preset. Outdoor water is excluded: irrigation and pool fills
    // should bypass the softener (WQA; Virginia Tech 442-670).
    DEMAND: {
      typical: {base: 60, perPerson: 30, scale: 1},
      saving: {base: 60, perPerson: 30, scale: 0.75},
      high: {base: 60, perPerson: 30, scale: 1.35}
    },
    BILL_DAYS: 30.4,            // a month of days, to turn a water bill into gallons a day

    // ---- resin capacity (R3, R5). Usable grains per cu ft = ANCHOR x f(dose), never above the tank's printed rating.
    // ANCHOR: 28,500 grains per cu ft at 15 lb salt per cu ft, what NSF/ANSI 44 certified complete home softeners
    // deliver (27,200 to 28,500); the resin-level figure without bed and system losses is about 30,000. f(dose): the
    // shape of the capacity-versus-dose curve of standard 8 percent crosslinked gel cation resin (the C100 class),
    // which five independent data sets agree on within about 0.04. Resin can come from more than one approved maker
    // (AJ 2026-10-14 19:00), so no single maker's curve is used. CQ's own resin data sheet is a deep-bed industrial
    // base curve and runs 15 to 25 percent higher; the section setting 'capacity_basis: rated' switches to CQ's
    // printed ratings (48,000 at 1.5 cu ft, 60,000 at 2.0, 30,000 per cu ft commercial) at 15 lb instead.
    ANCHOR: 28500,
    CURVE_FRACTION: [[4, 0.61], [5, 0.69], [6, 0.74], [8, 0.83], [10, 0.885], [12, 0.94], [15, 1.0]],
    // Leakage guard (R3 P7): hardness slipping through, mg/L as CaCO3 = B(dose) x K_TDS x K_TH, must stay at or
    // under 17.1 (1 gpg, the NSF/ANSI 44 end point). The dose steps up until it passes. Sources: CQ resin data sheet
    // Figs. 5 and 6 and a resin manufacturer's leakage charts.
    LEAK_BASE: [[4, 5.8], [5, 4.8], [6, 4.1], [8, 3.0], [10, 2.3], [12, 1.6], [15, 0.85]],
    LEAK_TDS: [[0, 0.4], [250, 0.6], [500, 1.1], [750, 1.9], [1000, 3.0], [1500, 5.9], [2000, 9.3]],
    LEAK_TH: [[0, 0.5], [10, 0.74], [20, 1.0], [30, 1.17], [40, 1.39], [60, 1.74]],
    LEAK_MAX: 17.1,
    TDS_ASSUMED: 250,
    // Capacity falls a little as dissolved solids rise (CQ resin data sheet Fig. 4 at about 20 gpm per sq ft:
    // 0.97 at 250 mg/L TDS, 0.94 at 500, 0.92 at 750, 0.91 at 1,000), normalised so a typical 250 mg/L counts as
    // the anchor and held flat past 1,000. Applied only when the visitor enters a TDS.
    TDS_FACTOR: [[250, 0.97], [500, 0.94], [750, 0.92], [1000, 0.91]],
    DP_STD: 15,                 // psi at the peak: the NSF/ANSI 44 and WQA rating basis (R2)
    DP_STRONG: 12,              // psi at the peak for the strongest-pressure priority (between R2's 10 psi option
                                // and the 15 psi standard, so it steps a home up one size, not three)
    DP_HARD: 20,                // psi: never recommend a tank past this at the home's peak (R2)
    // Salt settings in lb of salt per cu ft. NSF/ANSI 44's voluntary efficiency level is 3,350 grains per lb and
    // California's 4,000 (EPA WaterSense notice of intent, 2010); WQA best practice 4,000. 'full' is the maximum
    // useful dose (15 lb); on the independent basis it gives 28,500 grains per cu ft (42,750 on the 1.5 cu ft).
    SETTINGS: {saver: 6, balanced: 8, full: 15},
    DOSE_MAX: 15,
    KCL_FACTOR: 1.15,           // potassium chloride: about 10 to 28 percent more by weight (CQ resin page 10;
                                // WQA 13 to 15; chemistry ceiling 28); 1.15 used

    // ---- regeneration (R4). No standard publishes one right interval; there is a window and the choice inside it
    // is preference. Floor: short cycles waste the reserve and the fixed regeneration water. Ceiling: stagnant beds
    // grow bacteria, so a day override regenerates at least every 14 days.
    RESERVE_DAYS: 1,            // one day of the household's softened water held back (valve manuals)
    MAX_DAYS: 14,               // day override backstop
    MIN_DAYS: 4,                // 4 usable days + 1 reserve; below this the next tank is the better buy
    TARGET_DAYS: 7,             // 7 usable days + 1 reserve (Penn State's worked example lands at 6 to 7)
    // drain water per regeneration (gal) = 40 + cu ft x (1.08 x dose + 29.9): rated 4 GPM backwash for 10 minutes,
    // 10 percent brine, two bed volumes each of slow and fast rinse (resin maker's rinse rules). About 98 gal for a
    // 1.5 cu ft tank at 8 lb; real figures run about 30 percent lower to 55 percent higher.
    WATER_BASE: 40, WATER_PER_CUFT: 29.9, WATER_PER_LB: 1.08,
    NSF_EFFICIENCY: 3350,       // grains per lb, NSF/ANSI 44 efficiency rating level (EPA WaterSense NOI 2010)
    CA_EFFICIENCY: 4000,        // grains per lb, California and WQA best practice
    PRICE_SALT: 0.24,           // $ per lb, 40 lb pellets about $9.60 (two US retailers, 2026-10-14; NDSU $5 to $10)
    PRICE_KCL: 1.05,            // $ per lb, potassium chloride about $42 per 40 lb (2026-10-14)
    RATE_CITY_SEWER: 16.83,     // $ per 1,000 gal water plus sewer, US average (EPA WaterSense 2024 table)
    RATE_CITY_SEPTIC: 7.74,     // $ per 1,000 gal water only
    CHLORIDE_NACL: 0.607, CHLORIDE_KCL: 0.476,
    SODIUM_PER_GPG: 7.86,       // mg/L of sodium added per gpg of hardness removed (17.118 x 23 / 50.04)

    // ---- iron and manganese the softener itself removes (R3 P8 to P10, C2)
    METAL_FACTOR: 4,            // gpg of capacity reserved per mg/L of ferrous iron plus manganese: an empirical
                                // fouling allowance (the ion-exchange equivalent is only 0.105 gpg per mg/L); 4 is
                                // the figure in the one equipment source fetched, 5 the conservative end
    IRON_OK: 1,                 // Fe + Mn up to 1 mg/L: the softener alone (certified home units are rated to 1 ppm)
    IRON_FILTER_ADVISED: 2,     // above 2: an iron filter ahead is recommended (resin cleaner if the softener carries it)
    IRON_FILTER_REQUIRED: 4,    // above 4, or iron (mg/L) above the hardness (gpg): the filter goes first and the
                                // softener is sized on hardness alone; above 5 an oxidation stage too (Nebraska G1491)
    VERY_HARD: 60,
    IRON_DOSING: 5,
    MN_SOFTENER_MAX: 0.5,       // Nebraska G1714: softeners effective below 500 ug/L manganese
    PH_IRON_MIN: 6.7,           // Penn State: softener iron removal recommended only above pH 6.7
    PH_LOW: 6.5,                // below this a neutralizer comes first (EPA secondary range 6.5 to 8.5)
    NITRATE_LIMIT: 10,          // mg/L as N, EPA maximum contaminant level
    TDS_RO: 1000, TDS_NOTE: 500 // EPA secondary standard 500 mg/L
  };

  /* ---- peak flow (R2): IAPMO Study 4-2024 (UPC Appendix M) single-family probabilities and design flows */
  const FIX = {
    combo: {p: 0.055, q: 5.5},  // tub or tub/shower combination
    tub: {p: 0.010, q: 5.5},    // separate soaking tub
    shower: {p: 0.045, q: 2.0}, // stall shower, one head
    lav: {p: 0.020, q: 1.5},
    kitchen: {p: 0.020, q: 2.2},
    laundry: {p: 0.020, q: 2.0},
    toilet: {p: 0.010, q: 3.0},
    washer: {p: 0.055, q: 3.5},
    dishwasher: {p: 0.005, q: 1.3}
  };
  const HOSE_GPM = 2.0;          // one hose bibb on softened water, added as continuous flow (UPC Appendix M)
  const EXTRA_HEAD_GPM = 2.0;    // each extra shower head (WaterSense and IAPMO design flow per head)
  const PEAK_PCT = 0.99;         // 99th percentile of busy time (IAPMO); every priority uses it

  /* ---- tanks: printed limits (R5) and geometry (R2). comfort = the flow the tank handles at about 13 to 15 psi. */
  const AREA = d => Math.PI * Math.pow(d / 24, 2);   // bed area in sq ft from the tank's inside diameter (in)
  const SIZES = [
    {key: 'res15', cuft: 1.5, rated: 48000, kind: 'res', tank: '10 x 54 in', valve: '5600', cv: 5.0, area: AREA(10), depth: 33.0,
      printed: '9 to 11 GPM', comfort: 11, strong: 9, pipe: 1, select: {size: 1.5}},
    {key: 'res20', cuft: 2, rated: 60000, kind: 'res', tank: '12 x 52 in', valve: '5600', cv: 5.0, area: AREA(12), depth: 30.6,
      printed: '10 to 13 GPM', comfort: 13, strong: 10, pipe: 1, select: {size: 2}},
    {key: 'com30', cuft: 3, rated: 90000, kind: 'com', tank: '14 x 65 in', valve: '2510', cv: 4.9, area: AREA(14), depth: 33.7,
      printed: '15 GPM service', comfort: 15, strong: 12, pipe: 1, select: {flow: '15 GPM'}},
    {key: 'com40', cuft: 4, rated: 120000, kind: 'com', tank: '16 x 65 in', valve: '2850', cv: 13.2, area: AREA(16), depth: 34.4,
      printed: '20 GPM service', comfort: 20, strong: 20, pipe: 1.5, select: {flow: '20 GPM'}},
    {key: 'com70', cuft: 7, rated: 210000, kind: 'com', tank: '21 x 62 in', valve: '2850', cv: 13.2, area: AREA(21), depth: 34.9,
      printed: '35 GPM service', comfort: 35, strong: 35, pipe: 1.5, select: {flow: '35 GPM'}},
    {key: 'com100', cuft: 10, rated: 300000, kind: 'com', tank: '24 x 72 in', valve: '2900', cv: null, area: AREA(24), depth: 38.2,
      printed: '60 GPM service', comfort: 60, strong: 50, pipe: 2, select: {flow: '60 GPM'}},
    {key: 'com150', cuft: 15, rated: 450000, kind: 'com', tank: '30 x 72 in', valve: '2900', cv: null, area: AREA(30), depth: 36.7,
      printed: '75 GPM service', comfort: 75, strong: 75, pipe: 2, select: {flow: '75 GPM'}},
    {key: 'com200', cuft: 20, rated: 600000, kind: 'com', tank: '36 x 72 in', valve: '3150', cv: null, area: AREA(36), depth: 34.0,
      printed: '185 GPM service', comfort: 100, strong: 100, pipe: 2, select: {flow: '185 GPM'}}
  ];

  /* The visitor's priority. Each sets a salt setting, a target interval and how strictly flow is judged. */
  const PREFS = {
    balanced: {setting: 'balanced', target: C.TARGET_DAYS, flow: 'standard', label: 'Balanced'},
    cost: {setting: 'full', target: C.MIN_DAYS, flow: 'budget', label: 'Lowest upfront cost'},
    salt: {setting: 'saver', target: C.TARGET_DAYS, flow: 'standard', label: 'Least salt and water'},
    pressure: {setting: 'balanced', target: C.TARGET_DAYS, flow: 'strong', label: 'Strongest water pressure'}
  };

  /* ------------------------------------------------------------------------------------------ helpers */
  const num = v => (v === '' || v == null || !isFinite(+v)) ? null : +v;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const round = (v, d) => { const m = Math.pow(10, d || 0); return Math.round(v * m) / m; };
  function interp(table, x) {
    if (x <= table[0][0]) return table[0][1];
    for (let i = 1; i < table.length; i++) {
      if (x <= table[i][0]) {
        const [x0, y0] = table[i - 1], [x1, y1] = table[i];
        return y0 + (y1 - y0) * (x - x0) / (x1 - x0);
      }
    }
    return table[table.length - 1][1];
  }

  function toGpg(value, unit) {
    const v = num(value);
    if (v == null || v < 0) return null;
    const u = C.UNITS[unit] || C.UNITS.gpg;
    return v * u.factor;
  }
  function band(gpg) { return C.BANDS.find(b => gpg < b.max) || C.BANDS[C.BANDS.length - 1]; }

  /* ---- daily softened gallons */
  function dailyGallons(people, preset) {
    const d = C.DEMAND[preset] || C.DEMAND.typical;
    const n = clamp(Math.round(people || 1), 1, 40);
    return (d.base + d.perPerson * n) * d.scale;
  }

  /* ---- peak flow: exact convolution of binomial fixture groups in 0.1 gpm steps, zero demand excluded */
  function binom(n, p) {
    const out = [];
    let c = 1;
    for (let k = 0; k <= n; k++) {
      out.push(c * Math.pow(p, k) * Math.pow(1 - p, n - k));
      c = c * (n - k) / (k + 1);
    }
    return out;
  }
  function convolve(a, b) {
    const out = new Array(a.length + b.length - 1).fill(0);
    for (let i = 0; i < a.length; i++) if (a[i]) for (let j = 0; j < b.length; j++) out[i + j] += a[i] * b[j];
    return out;
  }
  function fixtureGroups(home) {
    const baths = clamp(Math.round(home.baths || 1), 1, 10);
    const style = home.bathStyle || 'combo';          // combo | shower | mixed
    const tubs = style === 'shower' ? 0 : style === 'mixed' ? Math.ceil(baths / 2) : baths;
    const showers = baths - tubs;
    const g = [];
    if (tubs) g.push(['combo', tubs]);
    if (showers) g.push(['shower', showers]);
    g.push(['lav', baths], ['toilet', baths], ['kitchen', 1], ['dishwasher', 1], ['washer', 1]);
    if (style !== 'shower') g.push(['laundry', 1]);
    return g;
  }
  function peakFlow(home, pct) {
    let dist = [1];
    fixtureGroups(home).forEach(([type, n]) => {
      const f = FIX[type], step = Math.round(f.q * 10), pmf = binom(n, f.p);
      const spread = new Array(n * step + 1).fill(0);
      pmf.forEach((pr, k) => { spread[k * step] += pr; });
      dist = convolve(dist, spread);
    });
    const busy = 1 - dist[0];
    let cum = 0, q = 0;
    for (let i = 1; i < dist.length; i++) {
      cum += dist[i] / busy;
      if (cum >= (pct || PEAK_PCT) - 1e-12) { q = i / 10; break; }
    }
    let peak = q;
    if (home.hose) peak += HOSE_GPM;
    if (home.extraHeads) peak += EXTRA_HEAD_GPM * clamp(home.extraHeads, 0, 6);
    const pump = num(home.pumpGpm);
    if (pump && pump > 0) peak = Math.min(peak, pump);
    return round(peak, 1);
  }

  /* ---- capacity of a tank at a salt setting */
  function tdsFactor(tds) { return tds == null ? 1 : Math.min(1, interp(C.TDS_FACTOR, Math.max(250, tds)) / 0.97); }
  function fraction(dose) { return interp(C.CURVE_FRACTION, clamp(dose, 4, C.DOSE_MAX)); }
  function grainsPerCuft(size, dose, basis) {
    const top = basis === 'rated' ? size.rated / size.cuft : Math.min(C.ANCHOR, size.rated / size.cuft);
    return top * fraction(dose);
  }
  function leakage(dose, tds, gpg) {
    return interp(C.LEAK_BASE, dose) * interp(C.LEAK_TDS, tds == null ? C.TDS_ASSUMED : tds) * interp(C.LEAK_TH, gpg);
  }
  // hardness above 20 gpg costs a little capacity (resin manufacturer chart, R3 P6)
  function hardnessFactor(gpg) { return gpg > 20 ? 1 - 0.0012 * (gpg - 20) : 1; }
  function capacityAt(size, settingKey, tds, opts) {
    const o = opts || {};
    let dose = C.SETTINGS[settingKey] || C.SETTINGS.balanced;
    let raised = false;
    // leakage guard: raise the dose until softened water stays at or under 1 gpg
    while (dose < C.DOSE_MAX && leakage(dose, tds, o.gpg || 10) > C.LEAK_MAX) { dose = Math.min(C.DOSE_MAX, dose + 1); raised = true; }
    const leak = leakage(dose, tds, o.gpg || 10);
    const perCuft = grainsPerCuft(size, dose, o.basis) * tdsFactor(tds) * hardnessFactor(o.gpg || 0);
    const grains = Math.min(size.rated, perCuft * size.cuft);
    return {dose, raised, leak: round(leak, 1), leakOk: leak <= C.LEAK_MAX, grains: Math.round(grains), perLb: Math.round(grains / (dose * size.cuft))};
  }

  /* ---- pressure drop at a flow: valve (Q / Cv)^2 plus a laminar bed term anchored at 4.5 psi for a 3 ft bed at
     10 gpm per sq ft (Wirth, WC&P 2014). Distributor and fittings excluded. */
  function pressureDrop(size, q) {
    const valve = size.cv ? Math.pow(q / size.cv, 2) : 0;
    const bed = 4.5 * ((size.depth / 12) / 3) * ((q / size.area) / 10);
    return valve + bed;
  }

  /* ------------------------------------------------------------------------------------------ main */
  function compute(input) {
    const i = input || {};
    const pref = PREFS[i.preference] || PREFS.balanced;
    const notes = [];
    const note = (id, level, title, text, extra) => notes.push(Object.assign({id, level, title, text}, extra || {}));

    /* water */
    const unit = C.UNITS[i.unit] ? i.unit : 'gpg';
    let gpg = toGpg(i.hardness, unit);
    let hardSource = gpg != null ? (i.hardSource === 'zip' ? 'zip' : 'measured') : null;
    if (gpg == null && i.useEstimate) { gpg = C.ASSUMED_GPG; hardSource = 'assumed'; }
    if (gpg == null) return {ready: false, version: VERSION};
    gpg = Math.min(gpg, 150);

    const fe = Math.max(0, num(i.iron) || 0), mn = Math.max(0, num(i.manganese) || 0);
    const ph = num(i.ph), nitrate = num(i.nitrate), tds = num(i.tds);
    const signs = new Set(i.signs || []);
    const parts = [{key: 'hardness', label: 'Hardness', gpg: round(gpg, 1), assumed: hardSource === 'assumed'}];

    // what goes ahead of the softener. Iron and manganese (R3 P10): up to 1 mg/L the softener carries them; above 2 an
    // iron filter ahead is advised; above 4, iron (mg/L) above the hardness (gpg), manganese over 0.5, iron with pH
    // under 6.7, rust from the tap, slime or a rotten egg smell: the filter goes first and the softener is sized on
    // hardness alone.
    const ahead = [];
    const metalTotal = fe + mn;
    const rust = signs.has('rust') || signs.has('black');
    const ironRequired = metalTotal > C.IRON_FILTER_REQUIRED || (fe > 0 && fe > gpg) || mn > C.MN_SOFTENER_MAX ||
      (metalTotal > 0 && ph != null && ph < C.PH_IRON_MIN) || signs.has('slime') || rust;
    const ironAdvised = !ironRequired && metalTotal > C.IRON_FILTER_ADVISED;
    const metalAhead = ironRequired || signs.has('smell') || (ironAdvised && i.ironFilter !== false) || i.ironFilter === true;
    if (metalAhead) ahead.push('metal');
    if (signs.has('tea')) ahead.push('tannin');
    const lowPh = (ph != null && ph < C.PH_LOW) || signs.has('bluegreen');
    if (lowPh) ahead.push('neutralizer');
    if (nitrate != null && nitrate >= C.NITRATE_LIMIT) ahead.push('nitrate');

    // trace iron and manganese the softener removes along with hardness
    let metalAdd = 0;
    if (!metalAhead && metalTotal > 0) {
      metalAdd = C.METAL_FACTOR * metalTotal;
      parts.push({key: 'metals', label: 'Iron and manganese', gpg: round(metalAdd, 1)});
    }
    // An acid neutralizer (calcite) dissolves into acidic water and adds hardness. Carbonate equilibrium (pKa1 6.35):
    // with HCO3 = alkalinity / 50 mmol/L and CO2 = HCO3 x 10^(6.35 - pH), calcite consumes x mmol/L of CO2 to reach
    // pH 7, where each mmol consumed also adds 2 meq of alkalinity: x = (CO2 - r x HCO3) / (1 + 2r), r = 10^(6.35 - 7).
    // Each mmol adds 100 mg/L hardness as CaCO3. Calcite works from pH 5.7 up; below that a soda ash feed is used, which
    // adds no hardness, and a specialist designs it (WQA residential design, C2). Unknown alkalinity is assumed at
    // 30 mg/L (labelled); added hardness is capped at 15 gpg, past which the design goes to a specialist.
    let neutAdd = 0, alkAssumed = false, phVeryLow = false;
    if (lowPh) {
      const phv = ph != null ? clamp(ph, 4, 7) : 6.0;
      phVeryLow = phv < 5.7;
      if (!phVeryLow) {
        let alk = num(i.alkalinity);
        if (alk == null || alk < 0) { alk = 30; alkAssumed = true; }
        const r = Math.pow(10, 6.35 - 7), hco3 = alk / 50, co2 = hco3 * Math.pow(10, 6.35 - phv);
        neutAdd = Math.min(15, Math.max(0, 100 * (co2 - r * hco3) / (1 + 2 * r)) / C.MGL_PER_GPG);
      }
      if (neutAdd > 0) parts.push({key: 'neutralizer', label: 'Added by the neutralizer', gpg: round(neutAdd, 1), assumed: alkAssumed || ph == null});
    }
    const compGpg = gpg + metalAdd + neutAdd;

    /* demand */
    const people = num(i.people) != null && num(i.people) >= 1 ? clamp(Math.round(num(i.people)), 1, 40) : 4;
    const bill = num(i.billGallons);
    const customGpcd = num(i.gpcd);
    let gallons, demandBasis;
    // a water bill: clamp to 40 to 2,000 gallons a day (R1 user range); more than twice the typical curve suggests a leak
    if (bill && bill > 0) { gallons = clamp(bill / C.BILL_DAYS, 40, 2000); demandBasis = 'bill'; }
    else if (customGpcd && customGpcd > 0) { gallons = customGpcd * people; demandBasis = 'custom'; }
    else { gallons = dailyGallons(people, i.usePreset); demandBasis = i.usePreset || 'typical'; }
    // working from home most weekdays adds about 10 gallons a person a day (R1, REU1999 employment effects)
    if (i.wfh && demandBasis !== 'bill') gallons += 10 * people;
    const grainsDay = gallons * compGpg;

    /* peak flow */
    const bathsIn = num(i.baths);
    const home = {baths: bathsIn != null && bathsIn >= 1 ? clamp(Math.round(bathsIn), 1, 10) : 2, bathStyle: ['combo', 'shower', 'mixed'].includes(i.bathStyle) ? i.bathStyle : 'combo',
      hose: i.hose === true, extraHeads: clamp(Math.round(num(i.extraHeads) || 0), 0, 6), pumpGpm: num(i.pumpGpm) > 0 ? num(i.pumpGpm) : null};
    const peak = peakFlow(home, PEAK_PCT);
    const peakStd = peak;

    /* every size */
    const kcl = i.regenerant === 'kcl';
    const rate = i.source === 'well' ? 0 : i.septic ? C.RATE_CITY_SEPTIC : C.RATE_CITY_SEWER;
    const price = kcl ? C.PRICE_KCL : C.PRICE_SALT;
    const basis = i.basis === 'rated' ? 'rated' : 'independent';
    // R4: regenerate at least weekly when the softener carries iron or manganese (fouling and bacteria)
    const maxDays = metalAdd > 0 ? 7 : C.MAX_DAYS;
    const sizes = SIZES.map(s => {
      const cap = capacityAt(s, pref.setting, tds, {gpg: compGpg, basis});
      const days = grainsDay > 0 ? cap.grains / grainsDay : Infinity;
      const timer = clamp(Math.floor(days) - C.RESERVE_DAYS, 0, maxDays);
      const metered = clamp(Math.floor(days), 0, maxDays);
      const saltRegen = cap.dose * s.cuft * (kcl ? C.KCL_FACTOR : 1);
      const perMonth = timer > 0 ? C.BILL_DAYS / timer : null;
      const waterRegen = C.WATER_BASE + s.cuft * (C.WATER_PER_LB * cap.dose + C.WATER_PER_CUFT);
      const saltMonth = perMonth ? perMonth * saltRegen : null;
      const waterMonth = perMonth ? perMonth * waterRegen : null;
      const costMonth = perMonth ? saltMonth * price + waterMonth / 1000 * rate : null;
      // Flow fit. Standard: the 99th percentile peak at or under the tank's comfort flow (top of its printed range,
      // about 13 to 15 psi). Strongest pressure: at most 12 psi at that peak. Budget: up to 15 percent over the
      // comfort flow with a stated pressure dip, never past 20 psi (R2).
      const comfort = s.comfort;
      const dp = pressureDrop(s, peak);
      let flowFit;
      const within = peak <= comfort && (s.cv ? dp <= C.DP_STD : true);
      if (pref.flow === 'strong') flowFit = within && (s.cv ? dp <= C.DP_STRONG : peak <= s.strong) ? 'ok' : 'no';
      else flowFit = within ? 'ok' : (pref.flow === 'budget' && peak <= comfort * 1.15) ? 'tight' : 'no';
      if (dp > C.DP_HARD) flowFit = 'no';
      const capFit = timer >= pref.target ? 'ok' : timer >= C.MIN_DAYS ? 'tight' : 'no';
      const oversized = s.kind === 'com' && s.area > 1.75 && !i.business;
      return {
        key: s.key, cuft: s.cuft, kind: s.kind, rated: s.rated, tank: s.tank, valve: s.valve, pipe: s.pipe, printed: s.printed,
        select: s.select, comfort, dose: cap.dose, grains: cap.grains, perLb: Math.round(cap.grains / saltRegen),
        days: isFinite(days) ? Math.floor(days * 10) / 10 : null, timer, metered, saltRegen: round(saltRegen, 1),
        saltMonth: saltMonth != null ? Math.round(saltMonth) : null,
        saltMonthMetered: metered > 0 ? Math.round(C.BILL_DAYS / metered * saltRegen) : null,
        waterRegen: Math.round(waterRegen),
        waterMonth: waterMonth != null ? Math.round(waterMonth) : null,
        costMonth: costMonth != null ? Math.round(costMonth) : null,
        chlorideMonth: saltMonth != null ? Math.round(saltMonth * (kcl ? C.CHLORIDE_KCL : C.CHLORIDE_NACL)) : null,
        efficiency: cap.grains / saltRegen >= C.CA_EFFICIENCY ? 'high' : cap.grains / saltRegen >= C.NSF_EFFICIENCY ? 'rated' : 'low',
        leak: cap.leak, leakOk: cap.leakOk, doseRaised: cap.raised,
        pressureDrop: round(dp, 1), flowFit, capFit, oversized,
        fit: (flowFit === 'no' || capFit === 'no') ? 'no' : (flowFit === 'tight' || capFit === 'tight') ? 'tight' : 'ok'
      };
    });

    // Above 2,000 mg/L TDS hardness leaks through at any salt dose (R3 P7): no tank fits, a specialist designs RO.
    const tdsRoute = tds != null && tds > 2000;
    if (tdsRoute) sizes.forEach(z => { z.leakOk = false; z.fit = 'no'; });
    // the match: the smallest size that is fully 'ok' for this priority (lowest cost also accepts 'tight')
    const okFor = s => pref.setting === 'full' ? s.fit !== 'no' : s.fit === 'ok';
    let pick = sizes.find(s => okFor(s) && !s.oversized) || sizes.find(okFor);
    // About a week between regenerations sets the recommendation (AJ 2026-10-15 02:21: show the bigger size; a bigger
    // bed uses less salt and drops less pressure at the same home). v1.1.0 removes the v1.0.x step-back to a 2.0 or a
    // smaller commercial tank at 4 to 6 days; that tank stays on screen, and Lowest cost still accepts it.
    const smaller = pick && pick.kind === 'com' && !tdsRoute
      ? sizes.slice(0, sizes.indexOf(pick)).filter(z => z.kind === 'res' && z.flowFit === 'ok' && z.capFit === 'tight').pop() : null;
    let beyond = false;
    if (!pick) { pick = sizes[sizes.length - 1]; beyond = true; }
    const picks = {};
    if (!i._inner) {
      Object.keys(PREFS).forEach(k => {
        if (k === (PREFS[i.preference] ? i.preference : 'balanced')) { picks[k] = pick.key; return; }
        const r = compute(Object.assign({}, i, {preference: k, _inner: true}));
        picks[k] = r.ready ? r.pick : null;
      });
    }

    /* what to show: both residential tanks, plus the commercial tank that fits when a home needs one */
    const res = sizes.filter(s => s.kind === 'res');
    const com = sizes.filter(s => s.kind === 'com');
    const shown = res.slice();
    if (pick.kind === 'com' || res.every(s => s.fit !== 'ok')) shown.push(pick.kind === 'com' ? pick : (com.find(s => s.fit === 'ok') || com[0]));

    /* notes: what else in the water */
    const pickSize = pick;
    if (gpg < 1 && hardSource !== 'assumed') note('soft', 'info', 'Your water is already soft', 'Under 1 gpg is soft water. You may not need a salt-based softener.');
    // AJ 2026-07-29 (product-truth.md): on city water the salt-free conditioner is the default pick below 7 gpg; the
    // softener size stays on screen because the visitor came to size one (the ruling's preference swap)
    else if (gpg < 7 && hardSource !== 'assumed' && i.source === 'city') note('citycond', 'route', 'On city water, a salt-free conditioner is the usual pick', 'Under 7 gpg on city water, most homes do well with a salt-free conditioner: it reduces scale with no salt and no drain water. The softener size here is for truly soft water.');
    else if (gpg < 3.5 && hardSource !== 'assumed') note('slight', 'info', 'Only slightly hard', 'Under 3.5 gpg, a softener is a comfort choice rather than a need. A salt-free conditioner is a lighter option for scale.');
    else if (gpg < 7 && hardSource !== 'assumed') note('slight', 'info', 'Moderately hard water', 'Between 3.5 and 7 gpg, softening is a comfort choice. A salt-free conditioner reduces scale buildup without salt; a softener gives truly soft water.');
    if (hardSource === 'assumed') note('assumed', 'info', 'Sized on an assumed ' + C.ASSUMED_GPG + ' gpg', 'A test strip or your water report gives your own number. The setting can be changed once you know it.');
    if (hardSource === 'zip') note('zip', 'info', 'Sized on your area\'s utility figure', 'Hardness varies across a service area. A quick test confirms your own tap.');
    if (metalAdd > 0) note('metals', 'info', 'Includes the iron your softener also removes', 'Ferrous iron and manganese use extra capacity and can foul resin, so we set aside ' + round(metalAdd, 1) + ' gpg for them.' + (metalTotal > C.IRON_OK ? ' Above 1 mg/L, add a resin cleaner to the salt.' : ''));
    if (metalAhead) {
      const smellOnly = signs.has('smell') && !ironRequired && !ironAdvised && metalTotal === 0;
      note('metal', ironRequired || smellOnly ? 'route' : 'info',
        smellOnly ? 'A filter for the rotten egg smell' : ironRequired ? 'Iron and manganese need their own filter' : 'An iron filter is the better setup',
        smellOnly ? 'Hydrogen sulfide is not removed by a softener and can foul the resin. A Metal Removal filter goes ahead of the softener.'
          : rust && metalTotal === 0 ? 'Rust or dark stains usually mean iron or manganese. Oxidized metals clog softener resin, so a Metal Removal filter goes first. A water test confirms the level.'
          : 'At this level iron and manganese foul softener resin. A Metal Removal filter goes ahead, and the softener is sized on hardness alone.');
    }
    if (metalTotal > C.IRON_DOSING) note('dosing', 'warn', 'High iron: an oxidation stage first', 'Above 5 mg/L, a chlorine feed ahead of the iron filter keeps it working. A specialist confirms the setup.');
    if (smaller) {
      const less = Math.round(smaller.saltMonth - pick.saltMonth), dip = pick.pressureDrop < smaller.pressureDrop - 0.4;
      note('weekly', 'info', 'Why the bigger tank', 'The ' + smaller.cuft.toFixed(1) + ' cu ft would regenerate every ' + smaller.timer + ' days here. The ' + pick.cuft + ' cu ft runs ' + pick.timer +
        (less >= 2 ? ', with about ' + less + ' lb less salt a month' + (dip ? ' and a smaller pressure dip' : '') : dip ? ', with a smaller pressure dip' : '') +
        '. The ' + smaller.cuft.toFixed(1) + ' stays in the sizes below as the lower cost choice.');
    }
    if (pick.flowFit === 'tight') note('flow-tight', 'info', 'A small pressure dip at the busiest minute', 'At ' + peak + ' GPM, the most your home is likely to draw at once, this tank drops about ' + Math.round(pick.pressureDrop) + ' psi. You may notice it when two tubs fill at once.');
    if (demandBasis === 'bill' && gallons > 2 * dailyGallons(people, 'typical')) note('bill-high', 'info', 'Your bill is high for your household', 'More than twice a typical home of ' + people + '. A running toilet or a leak is worth checking, since the softener treats that water too.');
    if (!tdsRoute && pickSize.dose > (C.SETTINGS[pref.setting] || 0)) note('leak', 'info', 'Salt setting raised for your water', 'With ' + (tds != null ? 'dissolved solids at ' + Math.round(tds) + ' mg/L' : 'water this hard') + ', some hardness slips through at low salt. We set ' + pickSize.dose + ' lb per cubic foot so softened water stays under 1 gpg.');
    if (signs.has('smellhot')) note('anode', 'info', 'A smell only on hot water', 'That usually comes from the water heater\'s anode rod, not your supply. A plumber can swap it.');
    if (ahead.includes('tannin')) note('tannin', 'route', 'Tea-colored water is tannin', 'A softener does not remove tannins and they foul its resin. A tannin system with a softener handles both.');
    if (lowPh && phVeryLow) note('neutralizer', 'route', 'Very acidic water: a pH feed comes first', 'Below about pH 5.7 a soda ash feed raises the pH instead of a calcite tank. It adds no hardness. A specialist sets it up with you.');
    else if (lowPh) note('neutralizer', 'route', 'Acidic water: a neutralizer comes first', 'Raising the pH adds hardness, so your softener is sized for about ' + round(compGpg, 1) + ' gpg' + (neutAdd >= 15 ? ', the most a calcite tank should add; a specialist confirms the design' : '') + '.' + (alkAssumed ? ' (Estimated: an alkalinity number makes it exact.)' : ''));
    if (nitrate != null && nitrate >= C.NITRATE_LIMIT) note('nitrate', 'health', 'Nitrate is above the EPA limit', 'Nitrate at ' + nitrate + ' mg/L is above the 10 mg/L drinking water limit. A softener does not remove it. It needs a nitrate system.');
    else if (nitrate != null && nitrate > 0) note('nitrate-ok', 'info', 'Nitrate is under the limit', 'A softener does not change nitrate, and yours is under the 10 mg/L drinking water limit.');
    if (tds != null && tds > 2000) note('tds', 'warn', 'Very high dissolved solids', 'Above 2,000 mg/L, hardness slips through a softener even at full salt. Whole house reverse osmosis is the fix. A specialist designs it.');
    else if (tds != null && tds > C.TDS_RO) note('tds', 'warn', 'High dissolved solids', 'Above 1,000 mg/L a softener still works, with a little less capacity; from about 1,500 the salt setting goes up to keep the water soft. Reverse osmosis is what lowers dissolved solids.');
    else if (tds != null && tds > C.TDS_NOTE) note('tds-note', 'info', 'Dissolved solids above the taste guideline', 'Over 500 mg/L, a drinking water RO at the kitchen tap is the usual fix. A softener does not lower dissolved solids.');
    if (i.source === 'city') note('chlorine', 'info', 'City water carries chlorine', 'Chlorine and chloramine do not change the size, but they slowly wear softener resin. A carbon stage protects it and takes out the taste.');
    if (kcl) note('kcl', 'info', 'Potassium instead of salt', 'Potassium chloride works the same way but takes about 15 percent more by weight and costs more per bag.');
    if (i.irrigation) note('irrigation', 'warn', 'Keep irrigation off the softener', 'Watering with softened water wastes salt and adds sodium to soil. Bypass the outside taps.');
    if (beyond) note('beyond', 'warn', 'Past our largest single tank', 'A twin or larger system fits this load. A specialist designs it with you.');
    // Water over 60 gpg (about 1,000 mg/L) is rare and usually carries high dissolved solids or iron as well: the size
    // stands, but a lab test and a specialist come first (AJ 2026-10-15 03:02, "what if it is really high").
    if (gpg > C.VERY_HARD && !tdsRoute) note('veryhard', 'warn', 'Very hard water: test it first', 'Water over 60 gpg is rare and usually carries other minerals too. A lab test confirms the number and measures what else can change the setup, and a specialist checks it with you.');
    if (pick.oversized) note('oversize', 'info', 'A big tank at home flows', 'Very large beds can channel at ordinary home flows. A specialist can confirm a twin or metered setup.');

    // sodium (or potassium) added to softened water by the ions actually exchanged: hardness, the neutralizer's add-back
    // and the true exchange equivalent of iron and manganese (0.105 gpg per mg/L), not the fouling allowance
    const exchanged = gpg + neutAdd + (metalAdd > 0 ? 0.105 * metalTotal : 0);
    const sodium = kcl ? null : round(exchanged * C.SODIUM_PER_GPG, 0);
    const potassium = kcl ? round(exchanged * 13.4, 0) : null;
    return {
      ready: true, version: VERSION,
      preference: i.preference && PREFS[i.preference] ? i.preference : 'balanced',
      setting: pref.setting,
      water: {gpg: round(gpg, 1), unit, source: hardSource, band: band(gpg), comp: round(compGpg, 1), parts},
      demand: {people, gallons: Math.round(gallons), basis: demandBasis, grainsDay: Math.round(grainsDay), grainsWeek: Math.round(grainsDay * 7)},
      flow: {peak, peakStd, baths: home.baths, style: home.bathStyle || 'combo', hose: home.hose},
      sizes, shown: shown.map(s => s.key), pick: pick.key, picks, beyond, basis,
      ahead, iron: {total: round(metalTotal, 2), required: ironRequired, advised: ironAdvised, ahead: metalAhead},
      notes, sodium, potassium, kcl
    };
  }

  return {VERSION, C, SIZES, PREFS, FIX, toGpg, band, dailyGallons, peakFlow, capacityAt, pressureDrop, grainsPerCuft, compute};
});
